// 주간 근무시간 — 모바일 전용 화면.
// 한 사람 = 한 줄(이름 · 12시간 기준선이 있는 야근/조근 미니 막대 · 야근+조근). 탭하면 상세 팝업.
// 요약 칩(전체/초과/주의)은 그대로 필터 역할도 한다.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Segmented, Select, Spin } from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined, DownOutlined, RightOutlined as CollapsedIcon } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;

const fmtH = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1));

function HoursBar({ checked, status, scale, limit }) {
  const pct = (v) => `${Math.min(100, (v / scale) * 100)}%`;
  return (
    <div className="wh-m-bar" aria-hidden="true">
      <div className={`wh-m-bar-ot ${status.toLowerCase()}`} style={{ left: 0, width: pct(checked) }} />
      <i className="wh-m-bar-limit" style={{ left: pct(limit) }} />
    </div>
  );
}

function EmployeeRow({ emp, deptName, onOpen, scale, limit }) {
  return (
    <button type="button" className={`wh-m-row is-${emp.status.toLowerCase()}`} onClick={() => onOpen(emp)}>
      <span className="wh-m-name">
        <span className="wh-m-name-text">{emp.name}</span>
        <small>{deptName || emp.rank || ''}</small>
      </span>
      <HoursBar checked={emp.checked_hours} status={emp.status} scale={scale} limit={limit} />
      <span className="wh-m-total">{fmtH(emp.checked_hours)}</span>
    </button>
  );
}

const WorkHoursMobile = ({
  data, loading, weekStart, onChangeWeek, part, onChangePart, scope, onRefresh, onOpenEmployee,
}) => {
  const [view, setView] = useState('dept'); // dept: 부서별 / rank: 시간순
  const [filter, setFilter] = useState('ALL'); // ALL | OVER | WARN
  const [collapsed, setCollapsed] = useState({});
  // 고정된 상단(주·필터) 높이를 재서 부서 머리줄이 그 바로 아래에 붙게 한다
  const topRef = useRef(null);
  const [topH, setTopH] = useState(110);
  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTopH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const limit = data?.limit_hours || 12;

  const pass = (e) => filter === 'ALL' || e.status === filter;

  const allEmployees = useMemo(() => (data?.departments || []).flatMap(
    (d) => d.employees.map((e) => ({ ...e, part: d.part, part_name: d.part_name })),
  ), [data]);

  // 막대 눈금: 최소 16시간, 그보다 많이 한 사람이 있으면 그만큼
  const scale = Math.max(16, ...allEmployees.map((e) => e.checked_hours + 1));

  const weekEnd = weekStart.add(6, 'day');
  const isThisWeek = weekStart.isSame(dayjs().subtract((dayjs().day() + 6) % 7, 'day'), 'day');
  const noScope = scope && !scope.all && scope.parts.length === 0;

  const rowProps = (e) => ({
    key: e.employee_no,
    emp: e,
    onOpen: onOpenEmployee,
    scale,
    limit,
  });

  const s = data?.summary;

  return (
    <div className="wh-m">
      <div className="wh-m-top" ref={topRef}>
        <div className="wh-m-week">
          <Button type="text" icon={<LeftOutlined />} onClick={() => onChangeWeek(weekStart.subtract(7, 'day'))} aria-label="이전 주" />
          <DatePicker
            value={weekStart}
            onChange={(d) => d && onChangeWeek(d)}
            allowClear={false}
            inputReadOnly
            variant="borderless"
            suffixIcon={null}
            format={() => `${weekStart.format('M.D')}(월) ~ ${weekEnd.format('M.D')}(일)`}
            className="wh-m-week-picker"
            popupClassName="wh-m-week-popup"
          />
          <Button type="text" icon={<RightOutlined />} onClick={() => onChangeWeek(weekStart.add(7, 'day'))} aria-label="다음 주" />
          {!isThisWeek && (
            <Button size="small" type="link" onClick={() => onChangeWeek(dayjs())}>이번 주</Button>
          )}
          <Button type="text" icon={<ReloadOutlined />} onClick={onRefresh} aria-label="새로고침" className="wh-m-refresh" />
        </div>

        <div className="wh-m-controls">
          <Select value={part} onChange={onChangePart} size="small" className="wh-m-part" popupMatchSelectWidth={false}>
            <Option value="All">{scope && !scope.all ? '담당 부서 전체' : '전체 부서'}</Option>
            {(scope?.parts || []).map((p) => <Option key={p.part} value={p.part}>{p.part_name}</Option>)}
          </Select>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[{ label: '부서별', value: 'dept' }, { label: '시간순', value: 'rank' }]}
          />
        </div>

        {s && (
          <div className="wh-m-chips">
            <button type="button" className={`wh-m-chip${filter === 'ALL' ? ' on' : ''}`} onClick={() => setFilter('ALL')}>
              전체 <b>{s.headcount}</b>
            </button>
            <button type="button" className={`wh-m-chip over${filter === 'OVER' ? ' on' : ''}`} onClick={() => setFilter(filter === 'OVER' ? 'ALL' : 'OVER')}>
              초과 <b>{s.over_count}</b>
            </button>
            <button type="button" className={`wh-m-chip warn${filter === 'WARN' ? ' on' : ''}`} onClick={() => setFilter(filter === 'WARN' ? 'ALL' : 'WARN')}>
              주의 <b>{s.warn_count}</b>
            </button>
          </div>
        )}
      </div>

      {noScope && (
        <div className="wh-m-empty">
          담당 부서가 지정되지 않았습니다.<br />관리자에게 권한 관리 → 사용자 부서 지정을 요청하세요.
        </div>
      )}

      <Spin spinning={loading}>
        <div className="wh-m-list">
          {view === 'rank' && (() => {
            const list = allEmployees.filter(pass).sort((a, b) => b.checked_hours - a.checked_hours);
            if (!list.length && data && !noScope) return <div className="wh-m-empty">해당 인원이 없습니다.</div>;
            return list.map((e) => <EmployeeRow {...rowProps(e)} deptName={e.part_name} />);
          })()}

          {view === 'dept' && (() => {
            const depts = (data?.departments || [])
              .map((d) => ({ ...d, shown: d.employees.filter(pass) }))
              .filter((d) => d.shown.length > 0);
            if (!depts.length && data && !noScope) return <div className="wh-m-empty">해당 인원이 없습니다.</div>;
            return depts.map((d) => {
              const isCollapsed = !!collapsed[d.part];
              return (
                <section key={d.part} className="wh-m-dept">
                  <button
                    type="button"
                    className="wh-m-dept-head"
                    style={{ top: 52 + topH }}
                    onClick={() => setCollapsed((c) => ({ ...c, [d.part]: !c[d.part] }))}
                  >
                    {isCollapsed ? <CollapsedIcon /> : <DownOutlined />}
                    <span className="wh-m-dept-name">{d.part_name}</span>
                    <span className="wh-m-dept-meta">{d.headcount}명 · 평균 {fmtH(d.avg_hours)}h</span>
                    {d.over_count > 0 && <span className="wh-m-badge over">{d.over_count}</span>}
                    {d.warn_count > 0 && <span className="wh-m-badge warn">{d.warn_count}</span>}
                  </button>
                  {!isCollapsed && d.shown.map((e) => <EmployeeRow {...rowProps(e)} />)}
                </section>
              );
            });
          })()}
        </div>
      </Spin>

      <div className="wh-m-legend">
        <span><i className="sw ok" />야근+조근 (특근 제외)</span>
        <span><i className="sw limit" />{limit}시간</span>
      </div>
    </div>
  );
};

export default WorkHoursMobile;
